/**
 * PickleballStrategy - Detection strategy for pickleball stroke counting
 *
 * Uses YOLO object detection + XGBoost ML model for stroke counting.
 * Draws bounding boxes for ball/paddle detection.
 * Uses 15fps setInterval loop with synthetic timestamps for replay parity.
 *
 * ByteTrack Integration:
 * - Maintains consistent trackIds across frames
 * - Interpolates missing detections using Kalman filter
 * - Separate trackers for ball and paddle (different motion patterns)
 */
import { BaseExerciseStrategy } from './BaseExerciseStrategy.js';
import { registerStrategy } from './exerciseStrategyRegistry.js';
import { ByteTracker } from '../utils/ByteTracker.js';

export class PickleballStrategy extends BaseExerciseStrategy {
  constructor() {
    super();
    // ByteTrack trackers - separate for ball and paddle (different motion patterns)
    // Ball moves fast - needs larger track buffer for brief disappearances
    this.ballTracker = new ByteTracker({ trackBuffer: 5 });
    // Paddle moves slower - shorter buffer is fine
    this.paddleTracker = new ByteTracker({ trackBuffer: 2 });
  }

  static get id() {
    return 'pickleball';
  }

  static get displayName() {
    return 'Pickleball';
  }

  static get detectorType() {
    return 'YOLO';
  }

  static get counterId() {
    return 'pickleball';
  }

  static get audioPreset() {
    return 'pickleball';
  }

  /**
   * Initialize strategy and reset trackers for new session
   */
  initialize(params) {
    super.initialize(params);
    // Reset ByteTrack trackers for fresh session
    this.ballTracker.reset();
    this.paddleTracker.reset();
  }

  getLabels() {
    return {
      title: 'Pickleball Stroke Counter',
      repNoun: 'Strokes',
      cameraTip: 'Position camera so paddle and ball are visible.',
      beepMessage: 'Every time I hit the ball correctly the beep sound will play',
      loadingModel: 'Loading YOLO + XGBoost models...',
      ball: 'Ball',
      paddle: 'Paddle',
      detected: 'Detected',
      notDetected: 'Not detected'
    };
  }

  getInitialStatus() {
    return { ball: false, paddle: false };
  }

  getInitialDebugState() {
    return {
      // XGBoost ML model status
      predictions: 0,
      lastProb: 0,
      bufferSize: 0,
      isModelLoaded: false,
      isLoading: true,
      loadError: null,
      // YOLO detector status
      yoloReady: false,
      yoloLoading: true,
      yoloError: null
    };
  }

  /**
   * Run YOLO detection
   */
  async runDetection(video) {
    return await this.detect(video);
  }

  /**
   * Build frame data from YOLO detections with ByteTrack tracking
   *
   * Returns arrays of all detected objects (balls/paddles) with tracking info.
   * ByteTrack adds:
   * - trackId: Consistent object identity across frames
   * - interpolated: True if position predicted (no YOLO detection this frame)
   *
   * For XGBoost, use getBestBall/getBestPaddle to select highest confidence.
   */
  buildFrameData(detections, timestamp) {
    // Extract all YOLO detections by class
    const ballDets = detections?.filter(d => d.class === 'ball') || [];
    const paddleDets = detections?.filter(d => d.class === 'paddle') || [];

    // Convert to ByteTracker input format (center coordinates)
    const ballInputs = ballDets.map(det => ({
      x: det.bbox.x + det.bbox.width / 2,
      y: det.bbox.y + det.bbox.height / 2,
      width: det.bbox.width,
      height: det.bbox.height,
      confidence: det.confidence
    }));

    const paddleInputs = paddleDets.map(det => ({
      x: det.bbox.x + det.bbox.width / 2,
      y: det.bbox.y + det.bbox.height / 2,
      width: det.bbox.width,
      height: det.bbox.height,
      confidence: det.confidence
    }));

    // Apply ByteTrack - returns tracked detections with trackId
    let trackedBalls = [];
    if (ballInputs.length > 0) {
      const tracked = this.ballTracker.update(ballInputs);
      trackedBalls = tracked.map(t => ({ ...t, interpolated: false }));
    } else {
      // No ball detections - update tracker and get interpolated positions
      this.ballTracker.update([]);
      const allTracks = this.ballTracker.getAllTracks();
      trackedBalls = allTracks
        .filter(t => t.interpolated)
        .map(t => ({ ...t, confidence: 0, interpolated: true }));
    }

    let trackedPaddles = [];
    if (paddleInputs.length > 0) {
      const tracked = this.paddleTracker.update(paddleInputs);
      trackedPaddles = tracked.map(t => ({ ...t, interpolated: false }));
    } else {
      // No paddle detections - update tracker and get interpolated positions
      this.paddleTracker.update([]);
      const allTracks = this.paddleTracker.getAllTracks();
      trackedPaddles = allTracks
        .filter(t => t.interpolated)
        .map(t => ({ ...t, confidence: 0, interpolated: true }));
    }

    return {
      timestamp,
      balls: trackedBalls,
      paddles: trackedPaddles,
      // Legacy single-object fields for backward compatibility with XGBoost
      // Selects highest confidence non-interpolated, or first interpolated
      ball: this.getBestDetection(trackedBalls),
      paddle: this.getBestDetection(trackedPaddles)
    };
  }

  /**
   * Get the best detection from an array (highest confidence real, or first interpolated)
   */
  getBestDetection(detections) {
    if (!detections || detections.length === 0) return null;

    // Prefer real detections over interpolated
    const real = detections.filter(d => !d.interpolated);
    if (real.length > 0) {
      // Return highest confidence
      return real.reduce((best, d) => d.confidence > best.confidence ? d : best);
    }

    // Fall back to first interpolated
    return detections[0];
  }

  /**
   * Extract status from frame data
   */
  getStatusFromFrameData(frameData) {
    return {
      ball: !!frameData?.ball,
      paddle: !!frameData?.paddle
    };
  }

  /**
   * Build debug state from counter state
   */
  buildDebugState(counterState) {
    return {
      predictions: counterState.predictionsThisSession || 0,
      lastProb: counterState.lastProbability || 0,
      bufferSize: counterState.bufferSize || 0,
      isModelLoaded: counterState.isModelLoaded,
      loadError: counterState.loadError
    };
  }

  /**
   * Draw all ball and paddle detections with ByteTrack info
   *
   * Visual indicators:
   * - Solid box: YOLO detection (real)
   * - Dashed box: Interpolated (Kalman predicted)
   * - trackId shown in label
   */
  drawFrame(ctx, video, detections, frameData, currentRepCount) {
    // Draw all tracked balls
    const balls = frameData?.balls || [];
    for (const ball of balls) {
      // Convert center coords back to top-left for drawing
      const x = ball.x - ball.width / 2;
      const y = ball.y - ball.height / 2;

      ctx.strokeStyle = ball.interpolated ? '#88ff88' : '#00ff00';
      ctx.lineWidth = 3;

      if (ball.interpolated) {
        ctx.setLineDash([5, 5]);
      }
      ctx.strokeRect(x, y, ball.width, ball.height);
      ctx.setLineDash([]);
    }

    // Draw all tracked paddles
    const paddles = frameData?.paddles || [];
    for (const paddle of paddles) {
      // Convert center coords back to top-left for drawing
      const x = paddle.x - paddle.width / 2;
      const y = paddle.y - paddle.height / 2;

      ctx.strokeStyle = paddle.interpolated ? '#ff88ff' : '#ff00ff';
      ctx.lineWidth = 3;

      if (paddle.interpolated) {
        ctx.setLineDash([5, 5]);
      }
      ctx.strokeRect(x, y, paddle.width, paddle.height);
      ctx.setLineDash([]);
    }

    // Draw rep count (readable on mirrored canvas)
    this.drawReadableText(ctx, `${currentRepCount}`, 20, 60, {
      fillStyle: '#fff',
      strokeStyle: '#000',
      lineWidth: 3,
      font: 'bold 48px Arial'
    });

    // Draw current engine indicator (top-right corner)
    if (this.currentEngine) {
      const engineLabel = this.currentEngine.includes('webgpu') ? '⚡ WebGPU' : '🔧 WebGL';
      const canvasWidth = ctx.canvas?.width || 640;
      this.drawReadableText(ctx, engineLabel, canvasWidth - 120, 30, {
        fillStyle: this.currentEngine.includes('webgpu') ? '#4CAF50' : '#FFC107',
        strokeStyle: '#000',
        lineWidth: 2,
        font: 'bold 16px Arial'
      });
    }
  }

  convertToFrameData(detections, timestamp) {
    const ballDet = detections?.find(d => d.class === 'ball');
    const paddleDet = detections?.find(d => d.class === 'paddle');

    return {
      timestamp,
      ball: ballDet ? {
        x: ballDet.bbox.x,
        y: ballDet.bbox.y,
        width: ballDet.bbox.width,
        height: ballDet.bbox.height,
        confidence: ballDet.confidence
      } : null,
      paddle: paddleDet ? {
        x: paddleDet.bbox.x,
        y: paddleDet.bbox.y,
        width: paddleDet.bbox.width,
        height: paddleDet.bbox.height,
        confidence: paddleDet.confidence
      } : null
    };
  }

  renderStatusDisplay(status, labels) {
    const l = labels || this.getLabels();

    return (
      <div style={{ display: 'flex', gap: '15px', marginTop: '10px', fontSize: '14px' }}>
        <span style={{ color: status.ball ? '#4CAF50' : '#f44336' }}>
          {l.ball}: {status.ball ? l.detected : l.notDetected}
        </span>
        <span style={{ color: status.paddle ? '#4CAF50' : '#f44336' }}>
          {l.paddle}: {status.paddle ? l.detected : l.notDetected}
        </span>
      </div>
    );
  }

  getModelVersionsDisplay(modelVersions) {
    const yoloDigest = modelVersions?.yolo?.modelJson?.replace('model-', '').replace('.json', '') || '?';
    // Handle both "xgboost-stroke-model-{hash}.json" and "xgboost-stroke-model.json" formats
    let xgboostDigest = modelVersions?.xgboost?.xgboostModel?.replace('.json', '').replace('xgboost-stroke-model-', '').replace('xgboost-stroke-model', '') || '?';
    if (xgboostDigest === '') xgboostDigest = 'default';
    return `Engine: ${this.engineDisplayLabel()} - YOLO: ${yoloDigest} - XGBoost: ${xgboostDigest}`;
  }

  renderDebugPanel(debugState) {
    return null;
  }

  supportsRecording() {
    return true;
  }

  getMaxRecordingFrames() {
    return 5000;
  }

  getRecordingFPS() {
    return 15;
  }

  initializeMovementLog(maxFrames) {
    // Pre-allocate array for YOLO (fixed-size buffer)
    return new Array(maxFrames);
  }

  sliceMovementLog(log, frameCount) {
    return log.slice(0, frameCount || log.length);
  }
}

// Register this strategy
registerStrategy(PickleballStrategy);
